import { useEffect, useState, useCallback } from 'react';
import { supabase } from '../lib/supabase.js';
import { today } from '../lib/dates.js';

// Manages today's daily_checkout row. Loads briefing too so the wizard knows
// the day's MITs. Realtime subscription keeps state synced across tabs.
export function useDailyCheckout() {
  const [briefing, setBriefing] = useState(null);
  const [checkout, setCheckout] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    const date = today();

    async function load() {
      const [{ data: b }, { data: c }] = await Promise.all([
        supabase.from('daily_briefing').select('*').eq('date', date).maybeSingle(),
        supabase.from('daily_checkout').select('*').eq('date', date).maybeSingle(),
      ]);
      if (!mounted) return;
      setBriefing(b ?? null);
      setCheckout(c ?? null);
      setLoading(false);
    }
    load();

    const channels = ['daily_briefing', 'daily_checkout'].map((tbl) =>
      supabase
        .channel(`co_${tbl}:${date}`)
        .on(
          'postgres_changes',
          { event: '*', schema: 'public', table: tbl, filter: `date=eq.${date}` },
          (payload) => {
            if (!payload.new) return;
            if (tbl === 'daily_briefing') setBriefing(payload.new);
            if (tbl === 'daily_checkout') setCheckout(payload.new);
          }
        )
        .subscribe()
    );

    return () => {
      mounted = false;
      channels.forEach((c) => supabase.removeChannel(c));
    };
  }, []);

  // Upsert one or more fields into today's daily_checkout row.
  const update = useCallback(async (patch) => {
    const date = today();
    const next = { date, ...(checkout ?? {}), ...patch };
    setCheckout(next);
    const { data, error } = await supabase
      .from('daily_checkout')
      .upsert(next, { onConflict: 'date' })
      .select()
      .single();
    if (error) console.error('daily_checkout upsert failed', error);
    else if (data) setCheckout(data);
    return data;
  }, [checkout]);

  return {
    loading,
    briefing,
    checkout,
    update,
    isComplete: !!(checkout && checkout.completed_at),
  };
}
